/**
 * Framework-freier Zustand fuer den `ChunkErrorHandler` (kein DI: der ErrorHandler wird frueh gebaut, ein
 * injizierter Router waere ein Zyklus).
 *
 * Scheitert ein Lazy-Chunk in der ERSTEN Navigation eines Deep-Links (wackliges Mobilnetz, offener Tab mit altem
 * `main` nach einem Deploy), setzt der Router per `restoreHistory` die URL auf `/{lang}/` zurueck, bevor der
 * ErrorHandler laeuft — ein `reload()` traefe dann die Website-Startseite statt den Poll. Deshalb merkt sich
 * `main.ts` die Einstiegs-URL, und bis zur ersten erfolgreichen Navigation laedt der Handler genau die neu
 * (Workorder vote-link-deploy-window-and-chunk-reload 2026-10-30). Spaetere In-App-Navigationen aendern die URL
 * erst nach Erfolg (`urlUpdateStrategy: 'deferred'`), dort stimmt `location.href` fuer ein `reload()`.
 */

export type EntryLocation = Pick<Location, 'pathname' | 'search' | 'hash'>;
export type ReloadLocation = Pick<Location, 'replace' | 'reload'>;

/** Einmal-Guard gegen Reload-Schleifen; wird erst nach einer erfolgreichen Navigation geloescht */
export const CHUNK_RELOAD_GUARD_KEY = 'chunk_error_reload';

// webpack: `ChunkLoadError: Loading chunk 5175 failed.\n(error: …)` (Prod und Dev, Chromium und WebKit gemessen
// 2026-10-30), benannte Chunks (`local`-Konfiguration) tragen statt der Zahl den Namen
const CHUNK_LOAD_FAILED = /Loading chunk [\w-]+ failed/;

let entryUrl: string | null = null;
let navigationSucceeded = false;

export function rememberEntryUrl(location: EntryLocation | undefined = typeof window === 'undefined' ? undefined : window.location): void {
    if (location) {
        entryUrl = `${location.pathname}${location.search}${location.hash}`;
    }
}

export function markNavigationSucceeded(storage: Pick<Storage, 'removeItem'> = sessionStorage): void {
    navigationSucceeded = true;
    try {
        storage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    } catch {
        // Storage gesperrt - dann gibt es auch keinen Guard
    }
}

/**
 * Der Router reicht den Fehler der Lazy-Route als unbehandelte Promise-Rejection durch; Zone verpackt sie in
 * `Error('Uncaught (in promise): ChunkLoadError: …')` mit dem Original unter `rejection`.
 */
export function isChunkLoadError(error: unknown): boolean {
    const candidates = [error, (error as { rejection?: unknown } | null | undefined)?.rejection];
    return candidates.some(candidate => {
        const { name, message } = (candidate ?? {}) as { name?: unknown; message?: unknown };
        return name === 'ChunkLoadError' || (typeof message === 'string' && CHUNK_LOAD_FAILED.test(message));
    });
}

/**
 * Laedt nach einem Chunk-Fehler einmal neu: vor der ersten erfolgreichen Navigation die Einstiegs-URL per
 * `replace`, danach per `reload`. `false`, wenn der Guard schon steht (oder Storage gesperrt ist) - dann kein
 * weiterer Versuch.
 */
export function reloadAfterChunkError(
    location: ReloadLocation = window.location,
    storage: Pick<Storage, 'getItem' | 'setItem'> = sessionStorage
): boolean {
    try {
        if (storage.getItem(CHUNK_RELOAD_GUARD_KEY)) {
            return false;
        }
        storage.setItem(CHUNK_RELOAD_GUARD_KEY, 'true');
    } catch {
        // ohne Storage kein Schleifenschutz - lieber nicht neu laden
        return false;
    }
    if (!navigationSucceeded && entryUrl) {
        location.replace(entryUrl);
    } else {
        location.reload();
    }
    return true;
}

/** nur fuer Tests: Modulzustand zuruecksetzen */
export function resetChunkReloadState(): void {
    entryUrl = null;
    navigationSucceeded = false;
}
